/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable security/detect-object-injection */
import {
  cloneElement,
  ReactElement,
  ReactNode,
  useCallback,
  useState,
  useMemo,
  JSXElementConstructor,
} from 'react';

export type WizardStore = Record<string, unknown>;
export type WizardStoreGet<T extends WizardStore> = <K extends keyof T>(
  key: K,
) => T[K];
export type WizardStoreSet<T extends WizardStore> = <K extends keyof T>(
  key: K,
  valueNew: T[K],
) => void;

export type WizardGo<I extends string | number | symbol | undefined> = (
  elementId?: I,
) => void;

export type WizardElementProps<
  T extends WizardStore,
  I extends string | number | symbol = string,
  G extends string | number | symbol | undefined = I,
> = {
  get: WizardStoreGet<T>;
  set: WizardStoreSet<T>;
  go: WizardGo<G>;
  prev?: I;
  curr?: I;
};

export type WizardElement<
  T extends WizardStore,
  I extends string,
> = ReactElement<WizardElementProps<T, I>>;

export type WizardElementsUncloned = Record<string, ReactElement>;

export type WizardProps<
  T extends WizardStore,
  E extends WizardElementsUncloned,
> = {
  Wrapper: ReactElement<{ children?: ReactNode }>;
  storeDefault: T;
  elements: E;
  elementIdEntry: keyof E;
  onClose?: () => void;
};

export function Wizard<
  T extends WizardStore,
  E extends WizardElementsUncloned,
>({
  Wrapper,
  storeDefault,
  elements,
  elementIdEntry,
  onClose = () => {
    /* */
  },
}: WizardProps<T, E>) {
  const [store, setStore] = useState(storeDefault);

  const [elementPrevIds, setElementPrevIds] = useState<(keyof E)[]>([]);
  const elementPrevId = useMemo(
    () =>
      elementPrevIds.length
        ? elementPrevIds[elementPrevIds.length - 1]
        : undefined,
    [elementPrevIds],
  );

  const [elementId, setElementId] = useState(elementIdEntry);
  const [element, setElement] = useState(elements[elementIdEntry]); // WizardElement<T, I>

  const set: WizardStoreSet<T> = useCallback(
    (key, valueNew) => {
      const contextNew = store;
      contextNew[key] = valueNew;
      setStore(contextNew);
    },
    [store],
  );
  const get: WizardStoreGet<T> = useCallback(
    (key) => {
      return store[key];
    },
    [store],
  );

  const go: WizardGo<keyof E> = useCallback(
    (elementIdNew) => {
      if (!elementIdNew) {
        onClose();
        return;
      }
      const indexNew = elementPrevIds.findIndex(
        (elementPrevId) => elementPrevId === elementIdNew,
      );
      setElementPrevIds((prevs) =>
        indexNew !== -1 ? prevs.slice(0, indexNew) : [...prevs, elementId],
      );
      setElementId(elementIdNew);
      setElement(elements[elementIdNew]);
    },
    [elementId, elementPrevIds, elements, onClose],
  );

  const wizardProps: WizardElementProps<T, keyof E> = useMemo(
    () => ({
      set,
      get,
      go,
      prev: elementPrevId,
      curr: elementId,
    }),
    [set, get, go, elementPrevId, elementId],
  );

  return cloneElement(Wrapper, {
    children: cloneElement<WizardElementProps<T, keyof E>>(
      element,
      wizardProps,
    ),
    ...wizardProps,
  });
}

export function withoutWizardProps<P extends WizardElementProps<any, any>>(
  element: JSXElementConstructor<P>,
): JSXElementConstructor<Omit<P, keyof WizardElementProps<any, any>>> {
  return element as unknown as JSXElementConstructor<
    Omit<P, keyof WizardElementProps<any, any>>
  >;
}
